import React, { useState } from 'react';
import { acceptCall, declineCall } from '../../../api/v2/calls';
import {
  expandSlotsToThirtyMinuteChoices,
  formatSlotRangeInTimezone,
  formatTimezoneAbbr,
} from '../../../helpers/timezone_utils';
import { failToast } from '../../ui_kit/utilities/fail_toast';

import styles from '../mentor_call_status/index.module.css';

const ONE_HOUR_MS = 3600 * 1000;

function isSlotBlocked(slot, blockedSlots) {
  if (!blockedSlots?.length) return false;
  const slotStart = new Date(slot.start_at).getTime();
  return blockedSlots.some(({ start_at }) => Math.abs(slotStart - new Date(start_at).getTime()) < ONE_HOUR_MS);
}

function formatSlotChoice(slot, timezone, tzAbbr) {
  const endISO = slot.end_at || new Date(new Date(slot.start_at).getTime() + 30 * 60 * 1000).toISOString();
  return formatSlotRangeInTimezone(slot.start_at, endISO, timezone, tzAbbr);
}

export default function PatientIncomingCallStatus({
  incomingCall,
  patientTimezone,
  mentorFirstName,
  blockedSlots,
  onAccepted,
  onDeclined,
}) {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  const [loading, setLoading] = useState(false);

  const proposedSlots = incomingCall?.proposed_slots || [];
  const slotChoices = expandSlotsToThirtyMinuteChoices(proposedSlots);
  const patientTz = patientTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const patientTzAbbr = formatTimezoneAbbr(patientTz);

  const handleAccept = () => {
    if (!selectedSlot) return;
    setLoading(true);
    acceptCall({ callId: incomingCall.uuid, scheduledAt: selectedSlot.start_at })
      .then((updatedCall) => {
        onAccepted(updatedCall);
      })
      .catch((err) => {
        failToast(err.message || 'Could not accept. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  const handleDecline = () => {
    setLoading(true);
    declineCall({ callId: incomingCall.uuid })
      .then(() => {
        onDeclined();
      })
      .catch((err) => {
        failToast(err.message || 'Could not decline. Please try again.');
      })
      .finally(() => {
        setLoading(false);
        setConfirmingDecline(false);
      });
  };

  if (confirmingDecline) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.label}>Decline this call request?</span>
        </div>
        <div className={styles.confirm_row}>
          <button className={styles.decline_btn} onClick={handleDecline} disabled={loading}>
            {loading ? 'Declining...' : 'Yes, decline'}
          </button>
          <button className={styles.counter_btn} onClick={() => setConfirmingDecline(false)} disabled={loading}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.title_row}>
        <PhoneIcon />
        <span className={styles.label}>{mentorFirstName} proposed these times — pick one to accept</span>
      </div>
      <div className={styles.slots}>
        {slotChoices.map((choice) => {
          const isBlocked = isSlotBlocked(choice, blockedSlots);
          return (
            <button
              key={choice.start_at}
              disabled={isBlocked}
              title={isBlocked ? 'Already booked' : undefined}
              className={`${styles.slot_btn} ${selectedSlot === choice ? styles.slot_btn_selected : ''} ${isBlocked ? styles.slot_btn_blocked : ''}`}
              onClick={() => !isBlocked && setSelectedSlot(choice === selectedSlot ? null : choice)}
            >
              {formatSlotChoice(choice, patientTz, patientTzAbbr)}
              {isBlocked && (
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#bbb',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    marginLeft: '6px',
                  }}
                >
                  Booked
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className={styles.actions}>
        <button className={styles.accept_btn} onClick={handleAccept} disabled={loading || !selectedSlot}>
          {loading ? 'Accepting...' : 'Accept selected time'}
        </button>
        <button className={styles.decline_btn} onClick={() => setConfirmingDecline(true)} disabled={loading}>
          Decline
        </button>
      </div>
    </div>
  );
}

function PhoneIcon() {
  return (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M20.9995 16.4767V19.1864C21.0037 20.2223 20.0723 21.0873 19.0265 20.9929C10.0001 21 3.00006 13.935 3.00713 4.96919C2.91294 3.92895 3.77364 3.00106 4.80817 3.00009H7.52331C7.96253 2.99577 8.38835 3.151 8.72138 3.43684C9.66819 4.24949 10.2772 7.00777 10.0429 8.10428C9.85994 8.96036 8.99696 9.55929 8.41026 10.1448C9.69864 12.4062 11.5747 14.2785 13.8405 15.5644C14.4272 14.9788 15.0274 14.1176 15.8851 13.935C16.9855 13.7008 19.7615 14.3106 20.5709 15.264C20.858 15.6021 21.0105 16.0337 20.9995 16.4767Z'
        stroke='#7a5c00'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
